/* tirvela-pdp.js
 * Tirvela PDP interactions — gallery, qty stepper, variant picker, ATC.
 *
 * Scoped to body.template-product. Reads product variant data from an
 * inline <script type="application/json" id="trv-pdp-product-data"> block
 * emitted by sections/tirvela-pdp-hero.liquid. Uses textContent only when
 * writing variant-derived strings into the DOM — never innerHTML — to
 * avoid XSS via shop admin metafields.
 *
 * ATC POSTs to {{ routes.cart_add_url }}.js (same-origin, Shopify Ajax Cart
 * API). On success it dispatches a 'cart:item-added' CustomEvent on document
 * so Horizon's cart-drawer can react if present. No third-party fetches.
 */
(function () {
  'use strict';

  if (!document.body || !document.body.classList.contains('template-product')) return;

  document.addEventListener('DOMContentLoaded', init, { once: true });

  function init() {
    const root = document.querySelector('[data-trv-pdp-hero]');
    if (!root) return;

    const dataEl = document.getElementById('trv-pdp-product-data');
    let product = null;
    try {
      product = dataEl ? JSON.parse(dataEl.textContent) : null;
    } catch (e) {
      product = null;
    }
    if (!product) return;

    const state = {
      product: product,
      currentVariant: pickInitialVariant(product),
      moneyFormat: (root.dataset.moneyFormat || '€{{amount}}'),
      cartUrl: root.dataset.cartUrl || '/cart/add.js',
      atcBusy: false,
    };

    setupGallery(root);
    setupQty(root);
    setupVariantPicker(root, state);
    setupAtc(root, state);
    setupStickyBar(root, state);
    setupBis(root, state);
  }

  /* ── Gallery ─────────────────────────────────────────────────── */

  function setupGallery(root) {
    const thumbs = root.querySelectorAll('[data-trv-pdp-thumb]');
    const slides = root.querySelectorAll('[data-trv-pdp-slide]');
    const counterCurrent = root.querySelector('[data-trv-pdp-gal-current]');
    const figNum = root.querySelector('[data-trv-pdp-fig-num]');
    const figText = root.querySelector('[data-trv-pdp-fig-text]');

    function activate(idx) {
      thumbs.forEach((t, i) => t.setAttribute('aria-current', i === idx ? 'true' : 'false'));
      slides.forEach((s, i) => s.setAttribute('aria-current', i === idx ? 'true' : 'false'));
      if (counterCurrent) counterCurrent.textContent = String(idx + 1).padStart(2, '0');
      if (figNum) figNum.textContent = String(idx + 1).padStart(2, '0');
      if (figText) {
        const slide = slides[idx];
        const img = slide ? slide.querySelector('img') : null;
        const alt = img ? (img.getAttribute('alt') || '') : '';
        figText.textContent = alt;
      }
    }

    thumbs.forEach((t, i) => {
      t.addEventListener('click', function (ev) {
        ev.preventDefault();
        activate(i);
      });
    });
  }

  /* ── Qty stepper ─────────────────────────────────────────────── */

  function setupQty(root) {
    const input = root.querySelector('[data-trv-pdp-qty-input]');
    const dec = root.querySelector('[data-trv-pdp-qty-dec]');
    const inc = root.querySelector('[data-trv-pdp-qty-inc]');
    if (!input) return;

    const min = 1;
    const max = 99;

    function clamp(n) {
      n = parseInt(n, 10);
      if (isNaN(n) || n < min) return min;
      if (n > max) return max;
      return n;
    }

    if (dec) {
      dec.addEventListener('click', function () {
        input.value = clamp(parseInt(input.value || '1', 10) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
    if (inc) {
      inc.addEventListener('click', function () {
        input.value = clamp(parseInt(input.value || '1', 10) + 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
    input.addEventListener('blur', function () {
      input.value = clamp(input.value);
    });
  }

  /* ── Variant picker ──────────────────────────────────────────── */

  function pickInitialVariant(product) {
    const url = new URL(window.location.href);
    const reqId = parseInt(url.searchParams.get('variant'), 10);
    if (reqId && Array.isArray(product.variants)) {
      const found = product.variants.find(function (v) { return v.id === reqId; });
      if (found) return found;
    }
    if (Array.isArray(product.variants)) {
      const avail = product.variants.find(function (v) { return v.available; });
      if (avail) return avail;
      return product.variants[0] || null;
    }
    return null;
  }

  function setupVariantPicker(root, state) {
    const buttons = root.querySelectorAll('[data-trv-pdp-variant-btn]');
    if (!buttons.length) return;

    function setActive(variant) {
      state.currentVariant = variant;
      buttons.forEach(function (b) {
        const id = parseInt(b.dataset.variantId, 10);
        const isThis = variant && id === variant.id;
        b.setAttribute('aria-pressed', isThis ? 'true' : 'false');
      });
      updatePrice(root, state);
      updateSku(root, variant);
      updateAvailability(root, variant);
      updateVariantInput(root, variant);
      updateUrl(variant);
    }

    buttons.forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.disabled) return;
        const id = parseInt(b.dataset.variantId, 10);
        const v = state.product.variants.find(function (x) { return x.id === id; });
        if (v) setActive(v);
      });
    });

    if (state.currentVariant) setActive(state.currentVariant);
  }

  function updatePrice(root, state) {
    const priceEl = root.querySelector('[data-trv-pdp-price]');
    const compareEl = root.querySelector('[data-trv-pdp-price-compare]');
    const unitEl = root.querySelector('[data-trv-pdp-price-unit]');
    const v = state.currentVariant;
    if (!v) return;

    if (priceEl) priceEl.textContent = formatMoney(v.price, state.moneyFormat);
    if (compareEl) {
      if (v.compare_at_price && v.compare_at_price > v.price) {
        compareEl.textContent = formatMoney(v.compare_at_price, state.moneyFormat);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
        compareEl.textContent = '';
      }
    }
    if (unitEl) {
      if (v.unit_price && v.unit_price_measurement) {
        const u = v.unit_price_measurement;
        const unit = (u.reference_value && u.reference_value > 1)
          ? (u.reference_value + ' ' + u.reference_unit)
          : u.reference_unit;
        unitEl.textContent = formatMoney(v.unit_price, state.moneyFormat) + ' / ' + unit;
        unitEl.hidden = false;
      } else {
        unitEl.hidden = true;
        unitEl.textContent = '';
      }
    }
  }

  function updateSku(root, variant) {
    if (!variant) return;
    const formCode = root.querySelector('[data-trv-pdp-form-code]');
    const lotVal = root.querySelector('[data-trv-pdp-lot]');
    if (formCode) {
      if (variant.sku) {
        formCode.textContent = 'Form. ' + variant.sku;
        formCode.hidden = false;
      } else {
        formCode.hidden = true;
      }
    }
    if (lotVal) {
      if (variant.sku) {
        lotVal.textContent = variant.sku;
        const row = lotVal.closest('[data-trv-pdp-lab-spec-lot]');
        if (row) row.hidden = false;
      } else {
        const row = lotVal.closest('[data-trv-pdp-lab-spec-lot]');
        if (row) row.hidden = true;
      }
    }
  }

  function updateAvailability(root, variant) {
    const atc = root.querySelector('[data-trv-pdp-atc]');
    const atcLabel = root.querySelector('[data-trv-pdp-atc-label]');
    const buyRow = root.querySelector('[data-trv-pdp-buy-row]');
    const bis = root.querySelector('[data-trv-pdp-bis]');
    const soldOut = !variant || !variant.available;
    if (bis && variant) bis.dataset.variantId = variant.id;
    if (buyRow) buyRow.hidden = soldOut;
    if (bis) bis.hidden = !soldOut;
    const stickyBtn = document.querySelector('[data-trv-pdp-sticky-btn]');
    if (stickyBtn) stickyBtn.textContent = soldOut ? 'Disponibile a breve' : 'Aggiungi';
    if (!atc || !atcLabel) return;
    if (!variant) {
      atc.disabled = true;
      atcLabel.textContent = atc.dataset.unavailableText || 'Non disponibile';
      return;
    }
    if (variant.available) {
      atc.disabled = false;
      atcLabel.textContent = atc.dataset.idleText || 'Aggiungi al carrello';
    } else {
      atc.disabled = true;
      atcLabel.textContent = atc.dataset.soldoutText || 'Disponibile a breve';
    }
  }

  function updateVariantInput(root, variant) {
    const input = root.querySelector('[data-trv-pdp-variant-input]');
    if (input && variant) input.value = String(variant.id);
    // Sync PayPal Pay Later widget to current variant
    const ptInput = root.querySelector('[data-trv-pdp-pt-variant]');
    if (ptInput && variant) {
      ptInput.value = String(variant.id);
      // Trigger Shopify payment terms re-render
      const ptForm = ptInput.closest('form');
      if (ptForm) ptForm.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function updateUrl(variant) {
    if (!variant || !window.history || !window.history.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url.toString());
  }

  /* ── ATC ─────────────────────────────────────────────────────── */

  function setupAtc(root, state) {
    const form = root.querySelector('[data-trv-pdp-atc-form]');
    if (!form) return;

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      if (state.atcBusy) return;
      const v = state.currentVariant;
      if (!v || !v.available) return;
      submitAtc(root, state, form);
    });
  }

  function submitAtc(root, state, form) {
    const atc = root.querySelector('[data-trv-pdp-atc]');
    const atcLabel = root.querySelector('[data-trv-pdp-atc-label]');
    if (!atc) return;
    state.atcBusy = true;
    atc.dataset.state = 'loading';
    if (atcLabel) atcLabel.textContent = atc.dataset.loadingText || 'Aggiungo…';

    const fd = new FormData(form);

    fetch(state.cartUrl, {
      method: 'POST',
      headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: fd,
      credentials: 'same-origin',
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (err) { throw err; });
        return r.json();
      })
      .then(function (item) {
        atc.dataset.state = 'success';
        if (atcLabel) atcLabel.textContent = atc.dataset.successText || 'Aggiunto';
        bumpCartCount(parseInt(fd.get('quantity') || '1', 10));
        showToast(state, item);
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { item: item, source: 'trv-pdp' },
          bubbles: true,
        }));
        setTimeout(function () {
          atc.dataset.state = 'idle';
          if (atcLabel) atcLabel.textContent = atc.dataset.idleText || 'Aggiungi al carrello';
          state.atcBusy = false;
        }, 1800);
      })
      .catch(function (err) {
        atc.dataset.state = 'idle';
        if (atcLabel) atcLabel.textContent = atc.dataset.idleText || 'Aggiungi al carrello';
        state.atcBusy = false;
        const msg = (err && err.description) ? err.description : 'Errore. Riprova.';
        showToast(state, null, msg);
      });
  }

  function bumpCartCount(by) {
    const els = document.querySelectorAll('.cart-count, [data-cart-count], [data-header-cart-count]');
    els.forEach(function (el) {
      const cur = parseInt(el.textContent || '0', 10);
      el.textContent = String((isNaN(cur) ? 0 : cur) + (by || 1));
    });
  }

  function showToast(state, item, errorMsg) {
    const toast = document.querySelector('[data-trv-pdp-toast]');
    if (!toast) return;
    const titleEl = toast.querySelector('[data-trv-pdp-toast-title]');
    const subEl = toast.querySelector('[data-trv-pdp-toast-sub]');
    if (errorMsg) {
      if (titleEl) titleEl.textContent = errorMsg;
      if (subEl) subEl.textContent = '';
    } else {
      if (titleEl) titleEl.textContent = toast.dataset.successTitle || 'Aggiunto al carrello';
      if (subEl) {
        const name = (item && (item.product_title || item.title)) || (state.product && state.product.title) || '';
        const price = (item && typeof item.price === 'number') ? formatMoney(item.price, state.moneyFormat) : '';
        subEl.textContent = [name, price].filter(Boolean).join(' · ');
      }
    }
    toast.dataset.visible = 'true';
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(function () {
      toast.dataset.visible = 'false';
    }, 2800);
  }

  /* ── Sticky mobile bar ───────────────────────────────────────── */

  function setupStickyBar(root, state) {
    const sticky = document.querySelector('[data-trv-pdp-sticky]');
    const details = root.querySelector('[data-trv-pdp-details]');
    const stickyBtn = sticky ? sticky.querySelector('[data-trv-pdp-sticky-btn]') : null;
    if (!sticky || !details) return;

    const obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        sticky.dataset.visible = e.isIntersecting ? 'false' : 'true';
      });
    }, { rootMargin: '-200px 0px 0px 0px' });
    obs.observe(details);

    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        const bis = root.querySelector('[data-trv-pdp-bis]');
        if (bis && !bis.hidden) { bis.click(); return; }
        const form = root.querySelector('[data-trv-pdp-atc-form]');
        if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    }

    const priceEl = sticky.querySelector('[data-trv-pdp-sticky-price]');
    if (priceEl) {
      const update = function () {
        if (state.currentVariant) {
          priceEl.textContent = formatMoney(state.currentVariant.price, state.moneyFormat);
        }
      };
      update();
      document.addEventListener('cart:item-added', update);
    }
  }

  /* ── Back-in-stock modal (BIS) ───────────────────────────────────
   * Custom on-brand modal. Submits to Shopify native /contact endpoint
   * (no app, no token). Shopify routes submission to store contact email.
   * Owner manually sends back-in-stock email when inventory returns.
   */

  function setupBis(root, state) {
    const bisBtn = root.querySelector('[data-trv-pdp-bis]');
    if (!bisBtn) return;

    const modal = buildBisModal();
    document.body.appendChild(modal);

    const panel = modal.querySelector('.trv-bis-modal__panel');
    const formView = panel.querySelector('[data-trv-bis-form-view]');
    const successView = panel.querySelector('[data-trv-bis-success-view]');
    const form = panel.querySelector('[data-trv-bis-form]');
    const emailInput = panel.querySelector('[data-trv-bis-email]');
    const consentInput = panel.querySelector('[data-trv-bis-consent]');
    const submitBtn = panel.querySelector('[data-trv-bis-submit]');
    const status = panel.querySelector('[data-trv-bis-status]');
    const closeBtn = panel.querySelector('[data-trv-bis-close]');
    const successProductEl = panel.querySelector('[data-trv-bis-success-product]');

    function open() {
      if (!state.currentVariant) return;
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      setTimeout(function () { emailInput.focus(); }, 50);
    }

    function close() {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
      // Reset for next open
      formView.hidden = false;
      successView.hidden = true;
      form.reset();
      status.hidden = true;
      status.className = 'trv-bis-modal__status';
      status.textContent = '';
      submitBtn.disabled = false;
      submitBtn.textContent = 'AVVISAMI';
    }

    function showStatus(type, msg) {
      status.hidden = false;
      status.className = 'trv-bis-modal__status' + (type ? ' trv-bis-modal__status--' + type : '');
      status.textContent = msg;
    }

    bisBtn.addEventListener('click', function (e) {
      e.preventDefault();
      open();
    });

    closeBtn.addEventListener('click', close);

    modal.addEventListener('click', function (e) {
      if (e.target === modal) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submitBis();
    });

    function submitBis() {
      const email = (emailInput.value || '').trim();
      if (!email || email.indexOf('@') === -1 || email.indexOf('.') === -1) {
        showStatus('error', 'Inserisci un indirizzo email valido.');
        return;
      }
      if (!consentInput.checked) {
        showStatus('error', 'Devi accettare il trattamento dei dati per procedere.');
        return;
      }

      const variant = state.currentVariant;
      const productTitle = (state.product && state.product.title) || '';
      const variantTitle = variant ? (variant.title || variant.option1 || '') : '';
      const productHandle = (state.product && state.product.handle) || '';
      const productId = (state.product && state.product.id) || '';
      const variantId = variant ? variant.id : '';
      const productUrl = window.location.origin + '/products/' + productHandle;

      const body = [
        '== TIRVELA BIS REQUEST ==',
        'Prodotto: ' + productTitle,
        'Variante: ' + variantTitle,
        'Handle: ' + productHandle,
        'Product ID: ' + productId,
        'Variant ID: ' + variantId,
        'URL: ' + productUrl,
        'Email cliente: ' + email,
        '',
        'Quando il prodotto torna disponibile, mandare email a: ' + email
      ].join('\n');

      submitBtn.disabled = true;
      submitBtn.textContent = 'INVIO IN CORSO…';
      showStatus('', '');
      status.hidden = true;

      const fd = new FormData();
      fd.append('form_type', 'contact');
      fd.append('utf8', '✓');
      fd.append('contact[email]', email);
      fd.append('contact[name]', 'BIS Request — ' + productTitle);
      fd.append('contact[subject]', 'BIS: ' + productTitle + ' (' + variantTitle + ')');
      fd.append('contact[body]', body);

      fetch('/contact', {
        method: 'POST',
        body: fd,
        headers: { 'Accept': 'text/html' },
        credentials: 'same-origin'
      }).then(function (res) {
        // Shopify returns 302 → follows redirect → final URL has ?contact_posted=true
        if (res.ok || (res.url && res.url.indexOf('contact_posted') !== -1)) {
          successProductEl.textContent = productTitle;
          formView.hidden = true;
          successView.hidden = false;
        } else {
          throw new Error('Submission failed: ' + res.status);
        }
      }).catch(function () {
        showStatus('error', 'Qualcosa non è andato. Riprova tra un istante.');
        submitBtn.disabled = false;
        submitBtn.textContent = 'AVVISAMI';
      });
    }
  }

  function buildBisModal() {
    const modal = document.createElement('div');
    modal.className = 'trv-bis-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Avvisami quando il prodotto torna disponibile');

    modal.innerHTML =
      '<div class="trv-bis-modal__panel">' +
        '<button type="button" class="trv-bis-modal__close" data-trv-bis-close aria-label="Chiudi">×</button>' +

        '<div data-trv-bis-form-view>' +
          '<h2 class="trv-bis-modal__heading">Avvisami quando torna</h2>' +
          '<p class="trv-bis-modal__body">Ti scriviamo una sola volta, appena questo prodotto rientra. Niente promo nascoste.</p>' +

          '<form data-trv-bis-form novalidate>' +
            '<label class="trv-bis-modal__field">' +
              '<span class="trv-pdp-visually-hidden">La tua email</span>' +
              '<input type="email" required class="trv-bis-modal__input" placeholder="La tua email" data-trv-bis-email autocomplete="email">' +
            '</label>' +

            '<label class="trv-bis-modal__consent">' +
              '<input type="checkbox" required data-trv-bis-consent>' +
              '<span>Acconsento al trattamento dei miei dati secondo la <a href="/pages/privacy-policy" target="_blank" rel="noopener">Privacy Policy</a> per ricevere una sola notifica al rientro del prodotto.</span>' +
            '</label>' +

            '<button type="submit" class="trv-bis-modal__submit" data-trv-bis-submit>AVVISAMI</button>' +

            '<div class="trv-bis-modal__status" data-trv-bis-status hidden></div>' +
          '</form>' +

          '<p class="trv-bis-modal__footer">Tirvela Boutique</p>' +
        '</div>' +

        '<div data-trv-bis-success-view hidden>' +
          '<div class="trv-bis-modal__success">' +
            '<h2 class="trv-bis-modal__success-heading">Grazie.</h2>' +
            '<p class="trv-bis-modal__success-body">Ti scriviamo via email appena <em data-trv-bis-success-product></em> torna disponibile.</p>' +
          '</div>' +
        '</div>' +
      '</div>';

    return modal;
  }

  /* ── Money formatting ────────────────────────────────────────── */

  function formatMoney(cents, format) {
    cents = parseInt(cents, 10);
    if (isNaN(cents)) return '';
    const value = (cents / 100).toLocaleString('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    if (!format || format.indexOf('{{amount}}') === -1) return '€ ' + value;
    return format.replace('{{amount}}', value).replace('{{ amount }}', value);
  }
})();
